import {
  ref,
  readonly,
  reactive,
  useContext,
  computed,
  useRoute,
  useRouter
} from "@nuxtjs/composition-api";
import { Logger } from "@wemade-vsf/core";
const state = reactive({
  term: "",
  page: 1,
  pageSize: 12,
  searchResults: null,
  selectedAggregations: {},
  isSearchOpen: false,
  loading: false
});
function useSearch(isSearchPage = false, pageSize = 12) {
  const error = ref({
    search: null
  });
  state.pageSize = pageSize;
  const { app } = useContext();
  const route = useRoute();
  const router = useRouter();
  const searchOriginalUrl = route.value.fullPath;
  const buildQueryString = () => {
    const routerQuery = router.resolve((window.location.pathname + window.location.search).slice(1)).route.query;
    return router.resolve({
      path: "/search",
      query: {
        ...routerQuery,
        term: state.term,
        ...state.selectedAggregations
      }
    });
  };
  const getFilters = () => {
    const filters = {};
    Object.keys(state.selectedAggregations).forEach((key) => {
      const numItems = state.selectedAggregations[key].length;
      if (numItems) {
        numItems === 1 ? filters[key] = { eq: state.selectedAggregations[key][0] } : filters[key] = { in: state.selectedAggregations[key] };
      }
    });
    return filters;
  };
  const updateHistory = () => {
    if (!isSearchPage) {
      if (!process.server && (window == null ? void 0 : window.history)) {
        const { href } = buildQueryString();
        window.history.replaceState({ searchOriginalUrl }, "Search", href);
      }
    }
  };
  const search = async (params) => {
    var _a;
    Logger.debug("useSearch/search", state);
    state.loading = true;
    try {
      const searchParams = {
        pageSize: state.pageSize,
        currentPage: state.page,
        search: state.term,
        filter: getFilters()
      };
      const { data } = await app.$vsf.$magento.api.products(searchParams);
      if ((params == null ? void 0 : params.append) && state.searchResults && (data == null ? void 0 : data.products)) {
        let products = [
          ...state.searchResults.items,
          ...data.products.items
        ];
        state.searchResults = {
          ...data.products,
          items: products
        };
      } else {
        state.searchResults = (_a = data == null ? void 0 : data.products) != null ? _a : null;
      }
    } catch (err) {
      Logger.error("useSearch/search", err);
      error.value.search = err;
    } finally {
      state.loading = false;
    }
  };
  const setSearchTerm = (term) => {
    state.term = term;
    /** Calling updateHistory() here causes frequent calls to window.history.replaceState()
     every time the search term changes (e.g., on every keystroke).
     When Cookiebot script is active, this triggers continuous history updates and
     can lead to browser freezes or unresponsiveness. Commenting this out prevents
     excessive history state mutations.
    */
    //updateHistory();
  };
  const changeFilter = (attribute_code, value) => {
    const selected = state.selectedAggregations[attribute_code] || [];
    const index = selected.indexOf(value);
    index > -1 ? selected.splice(index) : selected.push(value);
    let selectedAggregations = state.selectedAggregations;
    selectedAggregations[attribute_code] = selected;
    state.selectedAggregations = selectedAggregations;
    updateHistory();
    search();
  };
  const nextPage = async () => {
    state.page = state.page + 1;
    const params = {
      append: true
    };
    await search(params);
  };
  const openSearch = () => {
    state.isSearchOpen = true;
  };
  const closeSearch = () => {
    var _a, _b;
    state.isSearchOpen = false;
    state.term = "";
    state.searchResults = null;
    state.selectedAggregations = {};
    state.page = 1;
    if (!process.server && ((_b = (_a = window == null ? void 0 : window.history) == null ? void 0 : _a.state) == null ? void 0 : _b.searchOriginalUrl)) {
      window.history.replaceState({}, "Back", window.history.state.searchOriginalUrl);
    }
  };
  return {
    loading: computed(() => state.loading),
    error: readonly(error),
    searchResults: computed(() => state.searchResults),
    page: computed(() => state.page),
    pageSize: computed(() => state.pageSize),
    isSearchOpen: computed(() => state.isSearchOpen),
    term: computed(() => state.term),
    selectedAggregations: computed(() => state.selectedAggregations),
    search,
    nextPage,
    setSearchTerm,
    openSearch,
    closeSearch,
    changeFilter
  };
}
export {
  useSearch
};
